import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowOutwardRounded from '@mui/icons-material/ArrowOutwardRounded';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import BrandLockup from '../../components/BrandLockup';
import { useAuth } from '../../contexts/AuthContext';
import { PATH_AUTH, PATH_PAGE } from '../../routes/paths';
import { NAV_ITEMS } from './navConfig';

type Props = { open: boolean; onClose: () => void; dashboardPath: string };

export default function MobileNavigation({ open, onClose, dashboardPath }: Props) {
  const { pathname } = useLocation();
  const { account, isAuthenticated, isEmployer, isAdmin } = useAuth();
  const initials = account?.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        id: 'mobile-navigation',
        role: 'dialog',
        'aria-modal': true,
        'aria-label': 'Site menu',
        sx: {
          width: 352,
          maxWidth: 'calc(100% - 16px)',
          top: 8,
          right: 8,
          bottom: 8,
          height: 'calc(100% - 16px)',
          borderRadius: '22px',
          overflow: 'hidden',
          boxShadow: '0 24px 80px #131B4033',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: '#141B3D66', backdropFilter: 'blur(4px)' } } }}
    >
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.75, flexShrink: 0, borderBottom: '1px solid #ECEEF5' }}
        >
          <Box
            component={RouterLink}
            to={PATH_PAGE.root}
            onClick={onClose}
            aria-label="Job Victory home"
            sx={{ textDecoration: 'none' }}
          >
            <BrandLockup />
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close menu"
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#F3F4F9',
              border: '1px solid #EAECF3',
              borderRadius: '12px',
              color: '#69738B',
              '&:hover': { bgcolor: '#E9E8FA', color: 'primary.main' },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box component="nav" aria-label="Mobile navigation">
              {(['Explore', 'Company'] as const).map((section, sectionIndex) => (
                <Box key={section} sx={{ mt: sectionIndex ? 2 : 0 }}>
                  <Typography
                    component="h2"
                    sx={{
                      px: 1.5,
                      mb: 1,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '1.4px',
                      textTransform: 'uppercase',
                      color: '#6E7890',
                    }}
                  >
                    {section}
                  </Typography>
                  <List disablePadding sx={{ display: 'grid', gap: 0.5 }}>
                    {NAV_ITEMS.filter((item) => item.section === section).map((item) => {
                      const active =
                        item.path === PATH_PAGE.root
                          ? pathname === item.path
                          : pathname.startsWith(item.path);
                      return (
                        <ListItem key={item.path} disablePadding>
                          <ListItemButton
                            component={RouterLink}
                            to={item.path}
                            onClick={onClose}
                            selected={active}
                            aria-current={active ? 'page' : undefined}
                            sx={{
                              minHeight: 48,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: '11px',
                              color: '#4A566E',
                              '&:hover': { bgcolor: '#F5F6FB' },
                              '&.Mui-selected': {
                                bgcolor: '#4640D8',
                                color: 'white',
                                boxShadow: '0 5px 12px #4640D822',
                                '&:hover': { bgcolor: '#3933C3' },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 36, color: active ? '#E2E0FF' : '#858DA3' }}
                            >
                              <item.icon sx={{ fontSize: 21 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: active ? 600 : 500,
                              }}
                            />
                            {active && (
                              <ChevronRightRounded sx={{ fontSize: 19, color: '#DEDDFB' }} />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 'auto', pt: 2.5 }}>
              {isAuthenticated && account ? (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#F5F5FB',
                    border: '1px solid #ECECF6',
                    borderRadius: '15px',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: '#E7E5FD',
                        color: '#5147CD',
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: '#29344F' }}>
                        {account.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#69738B', mt: 0.25 }}>
                        {isAdmin ? 'Super admin' : isEmployer ? 'Employer' : 'Job seeker'} workspace
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    fullWidth
                    component={RouterLink}
                    to={dashboardPath}
                    onClick={onClose}
                    variant="contained"
                    startIcon={<SpaceDashboardOutlined />}
                    endIcon={<ArrowForwardRounded />}
                    sx={{
                      minHeight: 44,
                      borderRadius: '9px',
                      fontSize: 12,
                      justifyContent: 'flex-start',
                      '& .MuiButton-endIcon': { ml: 'auto' },
                    }}
                  >
                    Dashboard
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#F5F5FB',
                    border: '1px solid #ECECF6',
                    borderRadius: '15px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Epilogue, sans-serif',
                      fontSize: 15,
                      fontWeight: 700,
                      lineHeight: 1.5,
                      mb: 0.75,
                    }}
                  >
                    Your next chapter starts here.
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.6, mb: 1.75 }}
                  >
                    Find the right opportunity for you.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      component={RouterLink}
                      to={PATH_AUTH.login}
                      onClick={onClose}
                      variant="outlined"
                      sx={{ minHeight: 44, borderRadius: '8px', fontSize: 12 }}
                    >
                      Sign in
                    </Button>
                    <Button
                      fullWidth
                      component={RouterLink}
                      to={PATH_AUTH.signup}
                      onClick={onClose}
                      variant="contained"
                      sx={{ minHeight: 44, borderRadius: '8px', fontSize: 12 }}
                    >
                      Sign up
                    </Button>
                  </Stack>
                  <Button
                    fullWidth
                    component={RouterLink}
                    to={`${PATH_AUTH.signup}?role=employer`}
                    onClick={onClose}
                    endIcon={<ArrowOutwardRounded />}
                    sx={{ mt: 1, minHeight: 40, fontSize: 11, color: '#687189' }}
                  >
                    Hiring? Post a job
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Stack>
    </Drawer>
  );
}
